// Code Generated by Sidekick is for learning and experimentation purposes only.
import React, { useState, useRef } from "react";
import {
  Button,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { ProfileData } from "../../common/common.model";
import { ImageBadgeName, logout } from "../../common/common";
import { useAppSelector } from "../../hooks/store";
import { updateProjectMappings } from "../../services/common.service";
import MayoSvg from "../../assets/images/mayo_iris_toggle.svg";
import LumaSvg from "../../assets/images/luma_toggle.svg";

interface ProfileButtonProps {
  profileData: ProfileData;
  isOpen?: boolean;
  anchorEl?: HTMLElement | null;
  onOpen?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onClose?: () => void;
}

const ProfileButton: React.FC<ProfileButtonProps> = ({
  profileData,
  isOpen = false,
  anchorEl = null,
  onOpen,
  onClose,
}) => {
  const { loading } = useAppSelector(
    (state) => state.rootReducer.userInfoReducer.profileData
  );

  // Use local state only if external control is not provided
  const [localAnchorEl, setLocalAnchorEl] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [selectedToggle, setSelectedToggle] = useState(() => {
    return localStorage.getItem("selectedToggle") || "Mayo";
  });

  // Determine if we're using controlled or uncontrolled behavior
  const usingControlled = onOpen !== undefined && onClose !== undefined;
  const effectiveAnchorEl = usingControlled ? anchorEl : localAnchorEl;
  const effectiveOpen = usingControlled ? isOpen : Boolean(localAnchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (usingControlled) {
      if (onOpen) onOpen(event);
    } else {
      setLocalAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    if (usingControlled) {
      if (onClose) onClose();
    } else {
      setLocalAnchorEl(null);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleToggleClick = async (_: any, newToggle: string) => {
    if (newToggle !== null) {
      setSelectedToggle(newToggle);
      localStorage.setItem("selectedToggle", newToggle);
      try {
        const response = await updateProjectMappings(newToggle);
        if (response) {
          localStorage.setItem("projectName", response.project_name);
          window.location.reload();
        }
      } catch (error) {
        console.error("Error updating project name:", error);
      }
    }
  };

  const id = effectiveOpen ? "profile-popover" : undefined;

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={handleOpen}
        sx={{
          width: 32,
          height: 32,
          backgroundColor: "#3474E0",
          color: "#FFFFFF",
          borderRadius: "50%",
          minWidth: 0,
          padding: 0,
          fontSize: 12,
          fontWeight: "400",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textTransform: "uppercase",
        }}
      >
        <ImageBadgeName
          name={profileData?.given_name}
          height="32px"
          width="32px"
          fontSize="12px"
          loading={loading}
        />
      </Button>
      <Popover
        id={id}
        open={effectiveOpen}
        anchorEl={effectiveAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "24px",
            padding: "24px",
            marginLeft: "20px",
          },
        }}
      >
        <Typography
          variant="body2"
          color="#282B31"
          sx={{ fontWeight: "500", fontSize: "14px" }}
        >
          {profileData?.given_name} {profileData?.family_name}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            marginTop: 0.5,
            fontWeight: "400",
            fontSize: "10px",
            color: "#3474E0",
            marginBottom: 2,
          }}
        >
          {profileData?.email}
        </Typography>
        <ToggleButtonGroup
          value={selectedToggle}
          exclusive
          onChange={handleToggleClick}
          aria-label="Mayo and Luma Toggle"
          sx={{
            border: "1px solid #ABB1BA",
            borderRadius: "20px",
            "& .MuiToggleButtonGroup-grouped": {
              borderRadius: "20px",
              border: "none",
              "&:not(:first-of-type)": {
                borderRadius: "20px",
              },
              "&:not(:last-of-type)": {
                borderRadius: "20px",
              },
            },
            padding: "0.225rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          <ToggleButton
            value="Mayo"
            sx={{
              padding: "6px",
              width: "80px",
              textTransform: "capitalize",
              "&.Mui-selected": {
                color: "#1976d2",
                backgroundColor: "#F1F7FF",
                border: "1px solid #E4E7EC",
              },
            }}
          >
            <img src={MayoSvg} alt="Mayo Logo" style={{ marginRight: "4px" }} />
          </ToggleButton>
          <ToggleButton
            value="Luma"
            sx={{
              padding: "6px",
              width: "80px",
              textTransform: "capitalize",
              "&.Mui-selected": {
                color: "#1976d2",
                backgroundColor: "#F1F7FF",
                border: "1px solid #E4E7EC",
              },
            }}
          >
            <img
              src={LumaSvg}
              alt="Luma Logo"
              style={{ marginRight: "4px" }}
            />
          </ToggleButton>
        </ToggleButtonGroup>
        <div style={{ margin: "16px 0", borderTop: "1px solid #E0E0E0" }}></div>
        <Button
          variant="outlined"
          color="primary"
          sx={{
            border: "1px solid #3474E0",
            color: "#3474E0",
            borderRadius: "24px",
            fontWeight: "500",
            fontSize: "12px",
            textTransform: "none",
          }}
          onClick={handleLogout}
        >
          Logout
        </Button>
      </Popover>
    </>
  );
};

export default ProfileButton;
